import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PlanCreateModal } from "@/components/plans/PlanCreateModal";
import { PlanEditModal } from "@/components/plans/PlanEditModal";
import { PlansTable } from "@/components/plans/PlansTable";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { useCurrentUser } from "@/lib/hooks/auth";
import { useResetMfaErrorOnVerify } from "@/lib/hooks/mfa-verify-prompt";
import { useDeletePlan, usePlansList } from "@/lib/hooks/plans";
import { isReadOnlyRole, ROLE_LEVEL, type UserRole } from "@/lib/schemas/users";
import type { HostingPlanRead, PlansListFilters } from "@/lib/schemas/plans";

export const Route = createFileRoute("/_app/plans")({
  component: PlansPage,
});

function PlansPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useCurrentUser();
  const myRole = (me.data?.role ?? "peer_read") as UserRole;
  // Write capability mirror del BE `_can_write_plan` (reseller / admin / superuser).
  const canWrite = (ROLE_LEVEL[myRole] ?? 0) >= ROLE_LEVEL.reseller && !isReadOnlyRole(myRole);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<HostingPlanRead | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();

  const filters: PlansListFilters = useMemo(() => {
    const base: PlansListFilters = { limit: 25 };
    if (cursor) base.cursor = cursor;
    return base;
  }, [cursor]);

  const query = usePlansList(filters);
  const deleteMutation = useDeletePlan();
  useResetMfaErrorOnVerify(deleteMutation);

  async function onDelete(plan: HostingPlanRead): Promise<void> {
    const ok = window.confirm(
      t("plans.actions.delete_confirm", { name: plan.name }),
    );
    if (!ok) return;
    try {
      const result = await deleteMutation.mutateAsync(plan.id);
      if (result.decision_required && result.approval_id) {
        await navigate({
          to: "/approvals",
          search: { highlight: result.approval_id },
        });
      }
    } catch {
      // surfaced via deleteMutation.error red box below
    }
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("nav.plans")}</h1>
          {query.data?.total_estimate !== undefined && (
            <p className="text-sm text-muted-foreground">
              {t("plans.list.total", { count: query.data.total_estimate })}
            </p>
          )}
        </div>
        {canWrite && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 size-4" />
            {t("plans.list.new_plan")}
          </Button>
        )}
      </header>

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("plans.list.load_error")} — {query.error.message}
        </div>
      )}

      {deleteMutation.isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <div className="font-medium">{t("common.error_title")}</div>
          <ul className="ml-4 mt-1 list-disc space-y-0.5 text-xs">
            {formatApiError(deleteMutation.error).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <PlansTable
        plans={query.data?.data ?? []}
        isLoading={query.isLoading}
        canWrite={canWrite}
        onEdit={(p) => setEditTarget(p)}
        onDelete={(p) => void onDelete(p)}
      />

      {query.data && (query.data.next_cursor || query.data.prev_cursor) && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.prev_cursor}
            onClick={() => setCursor(query.data.prev_cursor ?? undefined)}
          >
            ← {t("common.back")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.next_cursor}
            onClick={() => setCursor(query.data.next_cursor ?? undefined)}
          >
            {t("common.next")} →
          </Button>
        </div>
      )}

      <PlanCreateModal open={createOpen} onOpenChange={setCreateOpen} />
      <PlanEditModal
        plan={editTarget}
        open={editTarget !== null}
        onOpenChange={(o) => {
          if (!o) setEditTarget(null);
        }}
      />
    </div>
  );
}
